import { useCallback, useEffect, useRef } from 'react'
import { useInterviewStore } from '@/store/interviewStore'
import { buildApiUrl, getApiHeaders } from '@/config/api'

type SaveType = 'auto' | 'manual'

interface DatabaseAutoSaveOptions {
  assignmentUuid?: string | null
  problemId: string
  sessionId?: string | null
  query: string
  executionResult?: any
  executionError?: string | null
  timeTakenSeconds?: number | null
  debounceMs?: number
}

interface SaveOverride {
  query?: string
  executionResult?: any
  executionError?: string | null
  timeTakenSeconds?: number | null
  saveType?: SaveType
}

export function useDatabaseAutoSave({
  assignmentUuid,
  problemId,
  sessionId,
  query,
  executionResult,
  executionError,
  timeTakenSeconds,
  debounceMs = 12000,
}: DatabaseAutoSaveOptions) {
  const store = useInterviewStore()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isSavingRef = useRef(false)
  const lastQueryRef = useRef<string>('')
  const lastResultRef = useRef<string>('')
  const isLockedRef = useRef(false)

  useEffect(() => {
    // Reset memoized state when assignment changes
    lastQueryRef.current = ''
    lastResultRef.current = ''
    isLockedRef.current = false
  }, [assignmentUuid])

  const saveProgress = useCallback(
    async (overrides?: SaveOverride) => {
      if (isSavingRef.current || isLockedRef.current) {
        return { success: false }
      }

      if (!assignmentUuid || !sessionId) {
        return { success: false }
      }

      const saveType: SaveType = overrides?.saveType || 'auto'
      const queryToSave = (overrides?.query ?? query ?? '').trim()
      const executionResultPayload = overrides?.executionResult ?? executionResult ?? null
      const executionErrorPayload = overrides?.executionError ?? executionError ?? null
      const timeTakenPayload = overrides?.timeTakenSeconds ?? timeTakenSeconds ?? null

      const resultSignature = executionResultPayload ? JSON.stringify(executionResultPayload) : ''

      if (
        saveType === 'auto' &&
        (!queryToSave ||
          (queryToSave === lastQueryRef.current && resultSignature === lastResultRef.current))
      ) {
        return { success: false }
      }

      try {
        isSavingRef.current = true

        const payload = {
          assignmentUuid: assignmentUuid,
          sessionId: sessionId,
          draftQuery: queryToSave,
          executionResult: executionResultPayload,
          executionError: executionErrorPayload,
          timeTakenSeconds: timeTakenPayload,
          saveType: saveType,
        }

        const response = await fetch(buildApiUrl('interview-sessions/public/save-database-progress', 'POST'), {
          method: 'POST',
          headers: {
            ...getApiHeaders(false),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        })

        if (!response.ok) {
          const error = await response.json().catch(() => ({}))
          console.error('❌ Database auto-save failed:', error)
          return { success: false }
        }

        const data = await response.json()
        const status = data?.data?.status

        if (status === 'locked') {
          console.warn('🔒 Database challenge submission locked; skipping further auto-saves.')
          isLockedRef.current = true
          return { success: true, locked: true }
        }

        lastQueryRef.current = queryToSave
        lastResultRef.current = resultSignature
        store.markDatabaseClean(problemId)

        return { success: true, locked: false }
      } catch (err) {
        console.error('❌ Database auto-save error:', err)
        return { success: false }
      } finally {
        isSavingRef.current = false
      }
    },
    [assignmentUuid, executionError, executionResult, problemId, query, sessionId, store, timeTakenSeconds],
  )

  const scheduleAutoSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (!assignmentUuid || !sessionId || isLockedRef.current) {
      return
    }

    timeoutRef.current = setTimeout(() => {
      saveProgress({ saveType: 'auto' })
    }, debounceMs)
  }, [assignmentUuid, debounceMs, saveProgress, sessionId])

  useEffect(() => {
    scheduleAutoSave()
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [query, executionResult, executionError, scheduleAutoSave])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveProgress({ saveType: 'manual' })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [saveProgress])

  useEffect(() => {
    const handleBeforeUnload = () => {
      saveProgress({ saveType: 'manual' })
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [saveProgress])

  const saveNow = useCallback(
    (overrides?: SaveOverride) => saveProgress({ ...overrides, saveType: overrides?.saveType || 'manual' }),
    [saveProgress],
  )

  return {
    saveNow,
    isSaving: isSavingRef.current,
    isLocked: isLockedRef.current,
  }
}
